import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ScientificAnalysisPatientNameDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource'
import { ScientificAnalysisEntityPatient } from '@/modules/scientific-analysis-module/services/scientific-analysis-dto-patient'
import { scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import { Nullable } from '@/shared/types/app.types'
import { store } from '@/store/store'
import { ColDef } from '@ag-grid-enterprise/all-modules'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'

export class ScientificAnalysisPatientDatasource extends ScientificAnalysisPatientNameDatasource<ScientificAnalysisEntityPatient> {
    getPatientColumns(): [string, string] {
        return ['middleName', 'surname2']
    }

    public static getEntityGridColumns(selector: string = '', hideAll:boolean = false): (ColDef | ExportedColDef | ExportedColGroupDef) {
        return {
            headerName: i18n.tc('scientificAnalysis.grid.results.patient.header'),
            children: [
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.name'),
                    field: `${selector}name`,
                    exportField: `${selector}name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.surname1'),
                    field: `${selector}surname1`,
                    exportField: `${selector}surname1`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.surname2'),
                    field: `${selector}surname2`,
                    exportField: `${selector}surname2`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerValueGetter: () => store.getters.parameters.global?.mainPatientIdentifierType.name,
                    filterParams: {
                        filterOptions: ['equals'],
                        defaultOption: 'equals',
                        newRowsAction: 'keep'
                    },
                    field: `${selector}patientIdentifier.paiIdentifier`,
                    exportField: `${selector}patientIdentifier.paiIdentifier`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.patientCenterIdentifier'),
                    exportField: `${selector}patientCenterIdentifier.center.code`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}patientCenterIdentifier.center.code`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.gender'),
                    field: `${selector}gender.name`,
                    exportField: `${selector}gender.name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.birthday'),
                    exportField: `${selector}birthdate`,
                    exportType: AgGridExportType.DATE,
                    field: `${selector}birthdate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.middleName'),
                    exportField: `${selector}middleName`,
                    field: `${selector}middleName`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    hide: true,
                    minWidth: 200,
                    flex: 1
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.phone'),
                    exportField: `${selector}contactInformation.mainPhone`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}contactInformation.mainPhone`,
                    sortable: false,
                    filter: false,
                    hide: true,
                    minWidth: 200,
                    flex: 1
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.otherPhone'),
                    exportField: `${selector}contactInformation.otherPhone1`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}contactInformation.otherPhone1`,
                    sortable: false,
                    filter: false,
                    hide: true,
                    minWidth: 200,
                    flex: 1
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.email'),
                    exportField: `${selector}contactInformation.email`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}contactInformation.email`,
                    sortable: false,
                    filter: false,
                    hide: true,
                    minWidth: 200,
                    flex: 1
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.address'),
                    exportField: `${selector}address.address`,
                    exportType: AgGridExportType.TEXT,
                    field: 'address.address',
                    sortable: false,
                    filter: false,
                    hide: true,
                    minWidth: 200,
                    flex: 1
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.patient.observations'),
                    exportField: `${selector}observations`,
                    exportType: AgGridExportType.TEXT,
                    field: 'observations',
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    hide: true,
                    flex: 1
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc('scientificAnalysis.grid.results.case.hasIncidents'),
                    exportField: `${selector}hasIncidents`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasIncidents`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                }
            ]
        }
    }

    public getGridColumns(): (ColDef | ExportedColDef | ExportedColGroupDef)[] {
        return [
            { type: 'customCheckbox' },
            ScientificAnalysisPatientDatasource.getEntityGridColumns()
        ]
    }

    public async paginator(_filterModel: CommonFilterModel, params: CommonParams, scrollId: string, totalElements: number): Promise<InfinitePaginatorResponse<ScientificAnalysisEntityPatient>> {
        const response: InfinitePaginatorResponse<ScientificAnalysisEntityPatient> = await scientificAnalysisService.search(this.scientificAnalysis, params, scrollId, totalElements) as InfinitePaginatorResponse<ScientificAnalysisEntityPatient>
        this.setTotalElements(response.scrollPage.totalElements)
        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response?.scrollPage?.scrollId)),
                size: response.scrollPage.size,
                totalElements: response.scrollPage.totalElements
            }
        }
    }

    public getPatAndLabId(selectedRow: ScientificAnalysisEntityPatient): [number, number] {
        const patId = selectedRow.id
        const labId = store.getters.labId
        return [patId, labId]
    }

    public getLabAndCaseId(_selectedRow: ScientificAnalysisEntityPatient): [number, Nullable<number>] {
        const labId = store.getters.labId
        return [labId, null]
    }

    public getHTMLFieldsList(data: ScientificAnalysisEntityPatient): string[] {
        return []
    }
}
